/**
 * Cart-level checkout gating for two independent Wilson rules:
 *
 * 1. Mixed cart rule: a cart containing both Product Draw items
 *    (custom.special_product_type includes "Product Draw") and non-draw items is
 *    invalid — the error banner is shown and all checkout CTAs (checkout button +
 *    accelerated checkout buttons) are disabled until resolved.
 *
 * 2. Restricted payment rule: a cart containing any item from the "Restricted
 *    Payments" collection (gift cards or Product Draw items — same collection
 *    Checkout Blocks uses for its payment-method-hide rules) must hide the
 *    express/wallet checkout buttons entirely, regardless of whether the cart is
 *    mixed. This is required because those buttons launch an accelerated flow
 *    outside Shopify's standard checkout, bypassing Checkout Blocks' payment
 *    customization entirely — checkout itself is NOT blocked here, only the
 *    express shortcut is removed from view (not just disabled/dimmed), since
 *    there's no error to explain to the shopper — it's simply not an available
 *    payment path for this cart.
 *
 * These two rules are independent: rule 1 governs the error banner and the main
 * checkout button; rule 2 only ever touches the express/wallet button row.
 */

import { ThemeEvents } from '@theme/events';

const DRAW_ATTR = 'data-product-draw';
const RESTRICTED_ATTR = 'data-restricted-payment';
const ERROR_SELECTOR = '[data-mixed-cart-error]';
const CHECKOUT_SELECTOR = '[data-checkout-button]';
const ADDITIONAL_CHECKOUT_SELECTOR = '.additional-checkout-buttons';
const HIDDEN_CLASS = 'hidden';

function detectMixedCart() {
  const rows = document.querySelectorAll('.cart-items__table-row[data-key]');

  let hasDrawItems = false;
  let hasNonDrawItems = false;
  let hasRestrictedItems = false;

  rows.forEach((row) => {
    if (row.dataset.productDraw === 'true') {
      hasDrawItems = true;
    } else {
      hasNonDrawItems = true;
    }

    if (row.dataset.restrictedPayment === 'true') {
      hasRestrictedItems = true;
    }
  });

  const isMixed = hasDrawItems && hasNonDrawItems;
  applyCartPaymentState(isMixed, hasRestrictedItems);
}

function applyCartPaymentState(isMixed, hasRestrictedItems) {
  const errorBanner = document.querySelector(ERROR_SELECTOR);

  if (errorBanner) {
    const shouldHide = !isMixed;
    // Guard against no-op writes: the mutation observer below watches these same
    // attributes, so writing unconditionally would re-trigger itself indefinitely.
    if (errorBanner.classList.contains(HIDDEN_CLASS) !== shouldHide) {
      errorBanner.classList.toggle(HIDDEN_CLASS, shouldHide);
      errorBanner.setAttribute('aria-hidden', String(shouldHide));
    }
  }

  const checkoutButton = document.querySelector(CHECKOUT_SELECTOR);

  if (checkoutButton instanceof HTMLButtonElement && checkoutButton.disabled !== isMixed) {
    checkoutButton.disabled = isMixed;
    checkoutButton.toggleAttribute('aria-disabled', isMixed);
  }

  // Apple Pay / Shop Pay / Google Pay / etc. — these are Shopify-rendered widgets
  // (often iframes) and bypass Checkout Blocks entirely, so the only reliable
  // guard is to remove them from view rather than disable/dim them in place.
  //
  // Hidden when the cart is an invalid mixed-draw cart (checkout itself is
  // blocked anyway), OR when any line item belongs to the Restricted Payments
  // collection — either case must not allow paying via an express/wallet method.
  const shouldHideExpress = isMixed || hasRestrictedItems;
  const additionalCheckoutButtons = document.querySelector(ADDITIONAL_CHECKOUT_SELECTOR);

  if (additionalCheckoutButtons && additionalCheckoutButtons.classList.contains(HIDDEN_CLASS) !== shouldHideExpress) {
    additionalCheckoutButtons.classList.toggle(HIDDEN_CLASS, shouldHideExpress);
    additionalCheckoutButtons.setAttribute('aria-hidden', String(shouldHideExpress));
  }
}

/**
 * Expose a function so cart-items-component.js can call it after AJAX updates.
 * Cart item rows must carry data-product-draw="true|false" and
 * data-restricted-payment="true|false" set from Liquid.
 */
window.WilsonCartProductDraw = { detect: detectMixedCart };

// Run on initial page load
detectMixedCart();

// Re-run on the cart:update event too, as a fast (if sometimes premature) first pass
document.addEventListener(ThemeEvents.cartUpdate, detectMixedCart);

// <cart-items-component> wraps both the line item rows and the checkout/summary
// area in both the cart page and the drawer, so observing it catches every morph
// that could touch either — including attribute-only changes (e.g. a stripped
// `disabled`) that don't add or remove any nodes.
const cartItemsComponent = document.querySelector('cart-items-component');
if (cartItemsComponent) {
  const cartObserver = new MutationObserver(() => detectMixedCart());
  cartObserver.observe(cartItemsComponent, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [DRAW_ATTR, RESTRICTED_ATTR, 'disabled', 'class'],
  });
}
